import React from 'react';
import {
  Grid,
  Typography,
  withStyles,
  createStyles,
  WithStyles,
  Theme,
  Link,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { RootState } from 'app/store/types';
import { getLoanOfficerName, getLoanOfficer } from 'app/reducers/loan-officer';
import { getMenu } from 'app/reducers/app-config';
import EqualHousingIcon from '../../../assets/img/broker/EqualHousingLender.svg';
import NmlsIcon from '../../../assets/img/broker/Nmls.png';
import FdicIcon from '../../../assets/img/icons/fdic.png';
import { LanguageSelector } from 'app/components/LanguageSelector';
import { isMultiLangEnabled } from 'app/reducers/app-config';
import { ClientType } from '@lenderful/domain';

// eslint-disable-next-line no-use-before-define
type ConnectProps = ReturnType<typeof mapStateToProps>;
// eslint-disable-next-line no-use-before-define
type HocProps     = WithStyles<typeof styles>;
type OwnProps     = {
  isMultiLangEnabled: boolean;
  clientName        : string;
  clientType        : ClientType;
};

type Props = ConnectProps & HocProps & OwnProps;

const mapStateToProps = (state: RootState) => ({
  loanOfficerName   : getLoanOfficerName(state),
  loanOfficer       : getLoanOfficer(state),
  menu              : getMenu(state),
  isMultiLangEnabled: isMultiLangEnabled(state),
});

const styles = (theme: Theme) => {
  const unit = theme.spacing();
  return createStyles({
    staticItem: {
      padding: `${unit}px ${unit * 2}px`,
    },
    icon: {
      margin: `${unit * 2}px ${unit * 1.75}px`,
    },
  });
};

const BrokerFooterComponent = (props: Props) => {
  const { classes, clientName, clientType, loanOfficerName, loanOfficer, menu, isMultiLangEnabled } = props;

  const { t }         = useTranslation();
  const currentYear   = new Date().getFullYear();
  const copyright     = '\u00A9';

  const textItems = [
    { title: 'Equal Housing Lender' },
    { title: `${loanOfficerName} #${loanOfficer.nmls}` },
  ];

  return (
    <footer>
      <Grid container classes={{ container: 'wideGrid' }}>
        <Grid item xs={12}>
          <Grid container justifyContent='center' alignItems='center'>
            <Grid item>
              <img className={classes.icon} src={NmlsIcon} alt='NMLS icon' />
            </Grid>
            {
              clientType === ClientType.BANK && (
                <Grid item>
                  <img className={classes.icon} src={FdicIcon} alt='FDIC icon' />
                </Grid>
              )
            }
            <Grid item>
              <img className={classes.icon} src={`${window.location.origin}${EqualHousingIcon}`} alt='Equal Housing Lender' />
            </Grid>
          </Grid>
          {
            isMultiLangEnabled || menu.length > 0 ? <Grid container justifyContent='center' classes={{ container: 'footerItemContainer' }}>
              { isMultiLangEnabled && <LanguageSelector footerSelector={false} /> }
              {
                menu.map((item, index) =>
                  <Grid item key={index}>
                    <Link
                      underline='hover'
                      href={item.url}
                      rel='noopener'
                      target='_blank'
                      style={{
                        fontSize: 14,
                        padding: '0px 15px',
                      }}
                    >
                      {t(`brokerFooter.linkItem.${index}`, {
                        title       : item.title,
                        defaultValue: '{{ title }}',
                      })}
                    </Link>
                  </Grid>)
              }
            </Grid> : null
          }
          <Grid container justifyContent='center' classes={{ container: 'footerItemContainer' }}>
            {
              textItems.map((item, index) =>
                <Grid item key={index} className={classes.staticItem}>
                  <Typography variant='body2' classes={{ body2: 'small' }}>
                    {t(`brokerFooter.textItem.${index}`, {
                      title       : item.title,
                      defaultValue: '{{ title }}',
                    })}
                  </Typography>
                </Grid>)
            }
          </Grid>
          <Grid
            container
            item
            xs      = {12}
            justifyContent='center'
            classes = {{ container: 'footerTextContainer' }}
          >
            <Typography align='center' variant='body2' classes={{ body2: 'small' }}>
              {t('brokerFooter.copyright', {
                clientName,
                copyright,
                currentYear,
                defaultValue:
                  '{{ copyright }} {{ currentYear }} {{ clientName }}. All rights reserved.',
              })}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </footer>
  );
};

export const BrokerFooter = compose<React.ComponentType<OwnProps>>(
  connect(
    mapStateToProps,
    null,
  ),
  withStyles(styles),
)(BrokerFooterComponent);
